import { BodyM } from 'components/Typography/Typography';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { translate } from 'components/Translation';
import styles from 'assets/jss/material-dashboard-pro-react/views/payments/payAnyBillStyle';
import { makeStyles } from '@material-ui/core';
import CustomInput from 'components/CustomInput/CustomInput';
import { PHONE_NUMBER_LENGTH } from 'utils/const/numbers';
import CustomButton from 'components/CustomButtons/Button';
import SimpleTable from 'components/SimpleTable/SimpleTable';
import AmountCell from './AmountCell';
import { TextCell } from 'components/Cells/Cells';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import NoBills from './NoBills';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import { finalize } from 'rxjs/operators';
import CustomRadio from 'components/CustomRadio/CustomRadio';
import { NumbersService } from 'services/numbers/numbers';
import { roundup } from 'components/PaymentMethod/helper/number';
import { usePageQuery } from 'commons/hooks/pageQuery';

// @ts-ignore
const useStyles = makeStyles(styles);

const MIN_BILLING_ACCOUNT_NO_LENGTH = 8;

interface IBillsTab {
	selectedBills: number[];
	setSelectedBills: React.Dispatch<React.SetStateAction<number[]>>;
	bills: types.bills.IExtendedBill[];
	loadingUnpaidBills: boolean;
	setBillingId: React.Dispatch<React.SetStateAction<string>>;
	radioType: types.bills.searchType;
	setRadioType: React.Dispatch<React.SetStateAction<types.bills.searchType>>;
	searchValue: { [key in types.bills.searchType]: string };
	setSearchValue: React.Dispatch<React.SetStateAction<{ [key in types.bills.searchType]: string }>>;
	billingAccountNo: string;
	setBillingAccountNo: React.Dispatch<React.SetStateAction<string>>;
	setAccountType: React.Dispatch<React.SetStateAction<string>>;
	showResults: boolean;
	hideResults: () => void;
	fetchBills: (billingId?: string) => void;
	organizationBillingIds: string[];
}

const BillsTab = ({
	selectedBills,
	setSelectedBills,
	bills,
	loadingUnpaidBills,
	setBillingId,
	radioType,
	setRadioType,
	searchValue,
	setSearchValue,
	setBillingAccountNo,
	billingAccountNo,
	showResults,
	hideResults,
	setAccountType,
	fetchBills,
	organizationBillingIds,
}: IBillsTab) => {
	const classes = useStyles();
	const [showError, setShowError] = useState<{ [key in types.bills.searchType]: boolean }>({
		mobileNumber: false,
		billingAccountNo: false,
		billNo: false,
	});
	const [loading, setLoading] = useState<boolean>(false);
	const isMounted = useRef(true);
	const inputValue = searchValue[radioType];
	const query = usePageQuery();
	const billingAccountNumber = query.get('accountNumber');

	const buttonDisabled = useMemo(() => {
		if (loading || loadingUnpaidBills) return true;
		if (radioType === 'mobileNumber') {
			return inputValue.length !== PHONE_NUMBER_LENGTH || showError.mobileNumber;
		}
		if (radioType === 'billingAccountNo') {
			return inputValue.length < MIN_BILLING_ACCOUNT_NO_LENGTH || showError.billingAccountNo;
		}
		return inputValue.length <= 0;
	}, [loading, inputValue, loadingUnpaidBills, radioType, showError]);

	const helperText = useMemo(() => {
		if (radioType === 'mobileNumber') {
			if (showError.mobileNumber) {
				return translate('bills.error.text');
			}
			if (inputValue.length !== PHONE_NUMBER_LENGTH) {
				return translate('bills.helper.text');
			}
		} else if (radioType === 'billingAccountNo') {
			return '';
		} else {
			return translate('bills.pay.any.bill.works.inside.organization');
		}
	}, [showError, inputValue, radioType]);

	const error = useMemo(() => {
		return (
			(radioType === 'mobileNumber' && showError.mobileNumber) ||
			(radioType === 'billingAccountNo' && showError.billingAccountNo)
		);
	}, [showError, radioType]);

	const seeBills = () => {
		hideResults();
		if (radioType === 'mobileNumber') {
			setBillingId(billingAccountNo);
			fetchBills(billingAccountNo);
		} else if (radioType === 'billingAccountNo') {
			setBillingId(searchValue.billingAccountNo);
			fetchBills(searchValue.billingAccountNo);
		} else {
			fetchBills();
		}
	};

	const changeRadioType = (key: types.bills.searchType) => {
		hideResults();
		setSelectedBills([]);
		setBillingId('');
		setRadioType(key);
	};

	const setInputValue = (event: React.FormEvent<HTMLInputElement>) => {
		let value = (event.target as HTMLInputElement).value;
		value = value.replace(/\D/g, '');
		if (radioType === 'mobileNumber' && value.length === PHONE_NUMBER_LENGTH) {
			setLoading(true);
			NumbersService.getAccountNumberInfo(value)
				.pipe(finalize(() => setLoading(false)))
				.subscribe(
					({ accountId, accountType }) => {
						if (isMounted.current) {
							setBillingAccountNo(accountId);
							setAccountType(accountType);
							setShowError((currentShowError) => ({
								...currentShowError,
								mobileNumber: false,
							}));
						}
					},
					() => {
						if (isMounted.current) {
							setShowError((currentShowError) => ({
								...currentShowError,
								mobileNumber: true,
							}));
						}
					}
				);
		} else if (
			// check if billing account number belongs to organization
			radioType === 'billingAccountNo' &&
			value.length < MIN_BILLING_ACCOUNT_NO_LENGTH
		) {
			setShowError((currentShowError) => ({
				...currentShowError,
				billingAccountNo: true,
			}));
		} else {
			setShowError((currentShowError) => ({
				...currentShowError,
				[radioType]: false,
			}));
		}
		setSearchValue((searchValue) => ({
			...searchValue,
			[radioType]: value,
		}));
		setBillingId('');
	};

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	/**
	 * condition to check whenever billingAccountNumber from Search param is changed
	 */
	useEffect(() => {
		if (billingAccountNumber && !isNaN(parseInt(billingAccountNumber))) {
			changeRadioType('billingAccountNo');
			setSearchValue((values) => ({
				...values,
				billingAccountNo: billingAccountNumber,
			}));
		}
	}, [billingAccountNumber]);

	const { billingAccountNo: searchedBillingAccountNo } = searchValue;

	/**
	 * condition to check whenever billingAccountNumber from Search param is changed and setSearchValue is called that triggers update of searchedBillingAccountNo
	 * we need to run it only once
	 */
	useEffect(() => {
		if (billingAccountNumber && searchedBillingAccountNo && searchedBillingAccountNo === billingAccountNumber) {
			seeBills();
		}
	}, [billingAccountNumber, searchedBillingAccountNo]);

	return (
		<div className={classes.tabWrapper}>
			<div className={classes.tabContentWrapper}>
				<BodyM disableMargin>{translate('bills.pay.in.seconds')}</BodyM>
				<BodyM disableMargin>{translate('bills.type.phone.number')}</BodyM>
				<div className={classes.radioContainer}>
					{(['mobileNumber', 'billingAccountNo', 'billNo'] as Array<types.bills.searchType>).map(
						(key: types.bills.searchType) => (
							<div className={classes.radio} key={key}>
								<CustomRadio
									label={translate(`bills.any.radio.${key}`)}
									checked={radioType === key}
									onChange={() => changeRadioType(key)}
								/>
							</div>
						)
					)}
				</div>
				<div className={classes.searchByNumberWrapper}>
					<div className={classes.numberInputWrapper}>
						<CustomInput
							inputProps={{
								value: inputValue,
								onChange: setInputValue,
							}}
							id="search-by-number-input"
							labelText={translate(`bills.any.radio.${radioType}.input`)}
							helperText={helperText}
							error={error}
							disabled={loading}
							formControlProps={{
								fullWidth: true,
							}}
						/>
					</div>
					<div className={classes.seeBillsWrapper}>
						<CustomButton disabled={buttonDisabled} onClick={seeBills} color="tertiary-light">
							{translate('bills.see.bills')}
						</CustomButton>
					</div>
				</div>
			</div>
			<div>
				<ConditionalRender
					show={loading || loadingUnpaidBills}
					onTrue={() => (
						<div>
							<CustomCircularProgress text={translate('loading')} loading transparent />
						</div>
					)}
				/>
				<ConditionalRender
					show={showResults}
					onTrue={() => (
						<ConditionalRender
							show={bills.length > 0}
							onTrue={() => (
								<SimpleTable
									idAccessor="billNo"
									columns={[
										{
											header: <div>{translate('bills')}</div>,
											accessor: 'unpaidAmount',
											width: 130,
											cell: ({ unpaidAmount, date }: types.bills.IExtendedBill) => (
												<AmountCell amount={roundup(unpaidAmount)} date={date} />
											),
										},
										{
											header: <div>{translate('payments.bills.number')}</div>,
											accessor: 'billNo',
											width: 120,
											cell: ({ billNo }: types.bills.IExtendedBill) => (
												<TextCell text={String(billNo)} />
											),
										},
										{
											header: <div>{translate('payments.bills.accountNumber')}</div>,
											accessor: 'billingId',
											width: 120,
											cell: ({ billingId }: types.bills.IExtendedBill) => (
												<TextCell text={String(billingId)} />
											),
										},
									]}
									data={bills}
									selectedItems={selectedBills}
									setSelectedItems={setSelectedBills}
									withCheckboxes
									striped
								/>
							)}
							onFalse={() => <NoBills />}
						/>
					)}
				/>
			</div>
		</div>
	);
};

export default BillsTab;
